import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Instagram,
    Youtube,
    ExternalLink,
    Calendar,
    Clock,
    User,
    Linkedin,
    BookOpen,
    Wrench,
    FileText,
    Users,
    Globe,
} from "lucide-react";
import SEO, {
    organizationJsonLd,
    websiteJsonLd,
    buildBreadcrumbsJsonLd,
} from "@/components/common/SEO";
import Stamp from "@/components/common/Stamp";
import ShareBar from "@/components/common/ShareBar";
import { SITE_META, LIBRO, NELSON_LINKS, CUENTOS } from "@/data/staticContent";
import { SITE_URL } from "@/lib/api";

// Pull-quotes crudos del tono NiTanAdicto
const PULL_QUOTES = [
    "Mis cicatrices pedían micrófono, y comprendí que el dolor también busca viralidad.",
    "Buscaba amor, pero lo disfrazaba de deseo en moteles.",
    "Buscaba calma, pero encontraba pipazos pixelados.",
    "Buscaba conexión, pero compraba atención con contador regresivo.",
];

// Iconos por proyecto (link key → lucide icon)
const LINK_ICONS = {
    linkedin: Linkedin,
    "libro-sina": BookOpen,
    elmapainverso: FileText,
    plansobrio: Users,
    cuentos: BookOpen,
    herramientas: Wrench,
    sinadicciones: Globe,
};

export default function Sobre() {
    const profileUrl = `${SITE_URL}/nelson-gonzalez-rodriguez`;

    // Author / Person schema con TODOS los links + GEO Chile + obras completas
    const personJson = {
        "@context": "https://schema.org",
        "@type": "Person",
        "@id": `${profileUrl}#person`,
        name: "Nelson González Rodríguez",
        givenName: "Nelson",
        familyName: "González Rodríguez",
        alternateName: ["@NiTanAdicto", "NiTanAdicto", "Nelson González", "Nelson González R."],
        description:
            "Autor chileno de 'El Principio del Mapa Inverso' y de la serie 'Cuentos para adictos' (4 cuentos: La casa de los lobos, El último barco, La ciudad de las puertas, Papá ¿qué es un adicto?). Desde su propia recuperación construyó SinAdicciones.org, plataforma que conecta a familias con tratamiento verificado e información sin letra chica en Latinoamérica. Creador de PlanSobrio.com. Deja herramientas gratuitas — tests, calculadora de costos, orientación confidencial y comunidad web — para que la ayuda esté disponible cuando él no está.",
        url: profileUrl,
        image: [SITE_META.portraitUrl, "https://www.sinadicciones.org/images/team/nelson_gonzalez.jpg"],
        nationality: { "@type": "Country", name: "Chile" },
        birthPlace: { "@type": "Country", name: "Chile" },
        homeLocation: {
            "@type": "Place",
            address: {
                "@type": "PostalAddress",
                addressCountry: "CL",
                addressRegion: "Chile",
            },
        },
        workLocation: [
            {
                "@type": "Place",
                name: "Chile",
                address: { "@type": "PostalAddress", addressCountry: "CL" },
            },
            {
                "@type": "AdministrativeArea",
                name: "Latinoamérica",
            },
        ],
        areaServed: [
            { "@type": "Country", name: "Chile" },
            { "@type": "AdministrativeArea", name: "Latinoamérica" },
        ],
        jobTitle: "Autor, comunicador y fundador",
        hasOccupation: [
            {
                "@type": "Occupation",
                name: "Autor de libros sobre adicción y recuperación",
                occupationLocation: { "@type": "Country", name: "Chile" },
                skills: "Adicción, recuperación, sobriedad consciente, prevención territorial, escritura narrativa breve, orientación a familias.",
            },
            {
                "@type": "Occupation",
                name: "Fundador de plataformas digitales de recuperación",
                occupationLocation: { "@type": "Country", name: "Chile" },
                skills: "Diseño de producto, información verificada de tratamientos, herramientas de autoevaluación, comunidad digital.",
            },
        ],
        worksFor: {
            "@type": "Organization",
            name: "SinAdicciones.org",
            url: "https://sinadicciones.org",
        },
        // Todos los libros y cuentos escritos por Nelson
        author: [
            {
                "@type": "Book",
                name: LIBRO.title,
                url: `${SITE_URL}/libro`,
                inLanguage: "es",
                bookFormat: "https://schema.org/EBook",
            },
            {
                "@type": "CreativeWorkSeries",
                name: "Cuentos para adictos",
                url: "https://www.sinadicciones.org/cuentos-para-adictos",
                inLanguage: "es",
                numberOfItems: 4,
                hasPart: CUENTOS.map((c) => ({
                    "@type": "ShortStory",
                    name: c.title,
                    headline: c.title,
                    alternativeHeadline: c.subtitle,
                    description: c.pitch,
                    url: c.url,
                    image: c.cover,
                    inLanguage: "es",
                    isAccessibleForFree: true,
                    author: { "@id": `${profileUrl}#person` },
                    audience: {
                        "@type": "Audience",
                        audienceType: c.audience === "family"
                            ? "Familias con niños o adolescentes"
                            : "Personas en recuperación de adicciones",
                    },
                })),
            },
        ],
        sameAs: [
            "https://www.linkedin.com/in/nelsongonzalezr/",
            "https://sinadicciones.org",
            "https://www.sinadicciones.org/libro",
            "https://www.sinadicciones.org/cuentos-para-adictos",
            "https://www.sinadicciones.org/herramientas",
            "https://www.sinadicciones.org/autor/nelson-gonzalez",
            "https://elmapainverso.com/descargar",
            "https://plansobrio.com",
            SITE_META.socials.instagram,
            SITE_META.socials.tiktok,
            SITE_META.socials.youtube,
            SITE_META.socials.original,
        ],
        knowsAbout: [
            "Adicción",
            "Recuperación de adicciones",
            "Sobriedad consciente",
            "Prevención territorial",
            "Tratamiento de adicciones Latinoamérica",
            "Craving",
            "Recaídas",
            "Orientación a familias en adicción",
            "Antipoesía",
            "Antiterapia",
            "Alcoholismo",
            "Salud mental",
        ],
        knowsLanguage: [{ "@type": "Language", name: "Spanish", alternateName: "es-CL" }],
        founder: [
            { "@type": "Organization", name: "SinAdicciones.org", url: "https://sinadicciones.org" },
            { "@type": "Organization", name: "PlanSobrio.com", url: "https://plansobrio.com" },
        ],
    };

    // FAQ específica de Nelson — SEO long-tail sobre quién es, qué escribe, dónde
    const nelsonFaqJson = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
            {
                "@type": "Question",
                name: "¿Quién es Nelson González Rodríguez?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "Nelson González Rodríguez es un autor chileno, fundador de SinAdicciones.org y de PlanSobrio.com. Escribió 'El Principio del Mapa Inverso' y la serie 'Cuentos para adictos'. Desde su propia recuperación construye herramientas gratuitas para conectar a familias con tratamiento verificado en Latinoamérica.",
                },
            },
            {
                "@type": "Question",
                name: "¿Qué libros ha escrito Nelson González Rodríguez?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "Dos obras principales: (1) 'El Principio del Mapa Inverso — Sobriedad con sentido' (2026), libro digital de 16 capítulos, y (2) 'Cuentos para adictos', una serie de 4 cuentos breves: La casa de los lobos, El último barco, La ciudad de las puertas y Papá ¿qué es un adicto? Todo se lee y descarga 100% gratis.",
                },
            },
            {
                "@type": "Question",
                name: "¿Dónde vive y desde dónde trabaja Nelson González?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "Es chileno. Vive en Chile y su trabajo cubre toda Latinoamérica a través de SinAdicciones.org (plataforma que opera en las 16 regiones de Chile) y PlanSobrio.com (plataforma hispanohablante).",
                },
            },
            {
                "@type": "Question",
                name: "¿Cómo puedo contactar a Nelson González?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "Vía LinkedIn (linkedin.com/in/nelsongonzalezr), a través de SinAdicciones.org, o mediante el formulario de contacto en elmapainverso.com/contacto. Nelson no es terapeuta de cabecera: para orientación en adicciones úsalo a través de las herramientas gratuitas de SinAdicciones.org.",
                },
            },
            {
                "@type": "Question",
                name: "¿Qué es la serie 'Cuentos para adictos'?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "Cuatro cuentos breves donde cada uno toma una frase que se repite en tratamientos y grupos de recuperación ('los dos lobos', 'quemar las naves', 'evita personas, lugares y cosas') y empieza la historia justo donde esa frase termina. Se leen gratis, sin registro. Tres cuentos son para la persona con la adicción; uno (Papá ¿qué es un adicto?) es para leer en familia.",
                },
            },
        ],
    };

    const profilePageJson = {
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        "@id": `${profileUrl}#profile`,
        url: profileUrl,
        name: "Acerca de Nelson · Nelson González Rodríguez",
        mainEntity: { "@id": `${profileUrl}#person` },
        primaryImageOfPage: {
            "@type": "ImageObject",
            url: SITE_META.portraitUrl,
            caption: "Retrato de Nelson González Rodríguez",
        },
        inLanguage: "es-CL",
    };

    const breadcrumbJson = buildBreadcrumbsJsonLd([
        { name: "Inicio", url: SITE_URL + "/" },
        { name: "Acerca de Nelson", url: profileUrl },
    ]);

    return (
        <>
            <SEO
                title="Acerca de Nelson · Nelson González Rodríguez · Autor chileno · Fundador de SinAdicciones.org"
                description="Nelson González Rodríguez: autor chileno de 'El Principio del Mapa Inverso' y de la serie 'Cuentos para adictos' (La casa de los lobos, El último barco, La ciudad de las puertas, Papá ¿qué es un adicto?). Fundador de SinAdicciones.org y PlanSobrio.com. Desde Chile, herramientas gratuitas para conectar familias con tratamiento verificado en Latinoamérica."
                path="/nelson-gonzalez-rodriguez"
                image={SITE_META.portraitUrl}
                imageAlt="Retrato de Nelson González Rodríguez, autor chileno y fundador de SinAdicciones.org"
                keywords="Nelson González Rodríguez, quién es Nelson González, autor chileno Nelson González, NiTanAdicto, El Principio del Mapa Inverso, Cuentos para adictos, La casa de los lobos, El último barco, La ciudad de las puertas, Papá qué es un adicto, cuentos sobre adicción, SinAdicciones.org, PlanSobrio, tratamiento adicciones Latinoamérica, tratamiento adicciones Chile, prevención territorial Chile, herramientas gratuitas adicción, orientación familias adicción, autor recuperación adicción, escritor sobriedad Chile, biografía Nelson González, contacto Nelson González"
                jsonLd={[
                    personJson,
                    profilePageJson,
                    breadcrumbJson,
                    nelsonFaqJson,
                    organizationJsonLd,
                    websiteJsonLd,
                ]}
                type="profile"
            />

            {/* ============== HERO EXPEDIENTE ============== */}
            <section
                className="relative grain border-b-2 border-[var(--danger)]"
                data-testid="sobre-hero"
                itemScope
                itemType="https://schema.org/Person"
            >
                <meta itemProp="nationality" content="Chile" />
                <meta itemProp="jobTitle" content="Autor, comunicador y fundador" />
                <link itemProp="worksFor" href="https://sinadicciones.org" />
                <link itemProp="sameAs" href="https://www.linkedin.com/in/nelsongonzalezr/" />
                <link itemProp="sameAs" href="https://sinadicciones.org" />
                <link itemProp="sameAs" href="https://plansobrio.com" />
                <link itemProp="sameAs" href="https://www.sinadicciones.org/cuentos-para-adictos" />
                <div className="hazard-stripes h-2 w-full" />
                <div className="container-x py-16 md:py-24 lg:py-28">
                    {/* Meta line */}
                    <div className="flex items-center gap-3 mb-8 flex-wrap font-mono text-[10px] uppercase tracking-[0.3em]">
                        <span className="text-[var(--danger)]">
                            Acerca de Nelson · Ficha oficial
                        </span>
                        <span className="dotted-div flex-1 min-w-[40px]" />
                        <span className="text-[var(--ink-muted)] inline-flex items-center gap-1.5">
                            <Calendar className="w-3 h-3" />
                            Actualizada 2026
                        </span>
                        <span className="text-[var(--ink-muted)] inline-flex items-center gap-1.5">
                            <Clock className="w-3 h-3" />
                            Lectura 2 min
                        </span>
                        <span className="text-[var(--ink-muted)] inline-flex items-center gap-1.5">
                            <User className="w-3 h-3" />
                            @NiTanAdicto
                        </span>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        {/* Left: Portrait */}
                        <div
                            className="lg:col-span-5 lg:sticky lg:top-32"
                            data-testid="sobre-portrait"
                        >
                            <div className="relative">
                                <div className="absolute inset-0 bg-[var(--danger)] -translate-x-3 -translate-y-3 md:-translate-x-5 md:-translate-y-5" />
                                <img
                                    src={SITE_META.portraitUrl}
                                    alt="Retrato de Nelson González Rodríguez, autor de El Principio del Mapa Inverso y fundador de SinAdicciones.org."
                                    className="relative w-full object-cover border-2 border-[var(--paper-2)]"
                                    loading="eager"
                                    itemProp="image"
                                />
                                <div className="absolute -top-3 -right-3 z-10">
                                    <Stamp size="md" rotation={10}>
                                        Caso 001
                                    </Stamp>
                                </div>
                                <div className="absolute -bottom-3 -left-3 z-10">
                                    <Stamp
                                        size="sm"
                                        rotation={-8}
                                        color="warning"
                                    >
                                        Constructor
                                    </Stamp>
                                </div>
                            </div>

                            {/* Identity card under portrait */}
                            <div className="dossier mt-8">
                                <div className="dossier-header">
                                    <span>Ficha de identidad</span>
                                    <span className="text-[var(--danger)]">
                                        Verificado
                                    </span>
                                </div>
                                <div className="dossier-content space-y-3 text-xs font-mono">
                                    <div className="flex justify-between gap-3 pb-2 border-b border-dashed border-[var(--ink-dim)]">
                                        <span className="text-[var(--ink-dim)] uppercase tracking-[0.2em]">
                                            Nombre
                                        </span>
                                        <span className="text-[var(--paper-2)] text-right">
                                            Nelson González Rodríguez
                                        </span>
                                    </div>
                                    <div className="flex justify-between gap-3 pb-2 border-b border-dashed border-[var(--ink-dim)]">
                                        <span className="text-[var(--ink-dim)] uppercase tracking-[0.2em]">
                                            Alias
                                        </span>
                                        <span className="text-[var(--paper-2)]">
                                            @NiTanAdicto
                                        </span>
                                    </div>
                                    <div className="flex justify-between gap-3 pb-2 border-b border-dashed border-[var(--ink-dim)]">
                                        <span className="text-[var(--ink-dim)] uppercase tracking-[0.2em]">
                                            Origen
                                        </span>
                                        <span className="text-[var(--paper-2)]">
                                            Chile
                                        </span>
                                    </div>
                                    <div className="flex justify-between gap-3 pb-2 border-b border-dashed border-[var(--ink-dim)]">
                                        <span className="text-[var(--ink-dim)] uppercase tracking-[0.2em]">
                                            Rol
                                        </span>
                                        <span className="text-[var(--warning)] text-right">
                                            Autor · Fundador
                                        </span>
                                    </div>
                                    <div className="flex justify-between gap-3">
                                        <span className="text-[var(--ink-dim)] uppercase tracking-[0.2em]">
                                            Funda
                                        </span>
                                        <a
                                            href="https://sinadicciones.org"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[var(--warning)] underline"
                                        >
                                            SinAdicciones.org
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Headline + intro */}
                        <div className="lg:col-span-7">
                            <p className="font-hand text-3xl md:text-4xl text-[var(--warning)] -rotate-2 inline-block mb-4">
                                ¡Hola, feed de cicatrices expuestas!
                            </p>
                            <h1
                                className="font-display text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.88] tracking-tight mb-6"
                                itemProp="name"
                            >
                                Nelson González
                                <br />
                                <span className="text-[var(--danger)]">
                                    Rodríguez
                                </span>
                            </h1>
                            <p className="font-display italic text-2xl md:text-3xl text-[var(--paper-2)] leading-[1.25] mb-8">
                                Salí del anonimato como{" "}
                                <strong className="text-[var(--danger)] not-italic font-bold">
                                    @NiTanAdicto
                                </strong>{" "}
                                porque las cicatrices pedían micrófono. Pero el trabajo real
                                empieza cuando yo no estoy.
                            </p>

                            <div
                                className="font-mono text-base md:text-lg text-[var(--paper)] leading-[1.75] space-y-5"
                                itemProp="description"
                            >
                                <p>
                                    Soy <strong className="text-[var(--paper-2)]">Nelson González Rodríguez</strong>, autor chileno de{" "}
                                    <em className="text-[var(--warning)]">El Principio del Mapa Inverso</em> y de{" "}
                                    <em className="text-[var(--warning)]">Cuentos para adictos</em>. Desde mi propia
                                    recuperación construí{" "}
                                    <a
                                        href="https://sinadicciones.org"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[var(--paper-2)] underline hover:text-[var(--warning)]"
                                    >
                                        SinAdicciones.org
                                    </a>
                                    , una plataforma que conecta a familias con tratamiento verificado
                                    e información sin letra chica en Latinoamérica.
                                </p>
                                <p>
                                    También soy creador de{" "}
                                    <a
                                        href="https://plansobrio.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[var(--paper-2)] underline hover:text-[var(--warning)]"
                                    >
                                        PlanSobrio.com
                                    </a>
                                    , la plataforma de habla hispana para conectar con personas en
                                    recuperación o que viven una vida sobria. Colaboro con trabajo
                                    preventivo territorial.
                                </p>
                                <p className="text-[var(--paper-2)] font-bold">
                                    No soy terapeuta de cabecera ni vocero cotidiano: mi
                                    trabajo es dejar herramientas que se puedan usar cuando yo no
                                    estoy.
                                </p>
                                <p>
                                    El foco está en informar a toda la región y darle a quien
                                    sufre —y a su familia— un mapa claro: alternativas reales
                                    de tratamiento, guías paso a paso y{" "}
                                    <a
                                        href="https://www.sinadicciones.org/herramientas"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[var(--paper-2)] underline hover:text-[var(--warning)]"
                                    >
                                        herramientas gratuitas
                                    </a>{" "}
                                    (tests de autoevaluación, calculadora de costos, orientación
                                    confidencial y una comunidad web) para comparar sin letra chica y
                                    dar el primer paso, incluso cuando no tienen a nadie que los guíe.
                                </p>
                                <p>
                                    Construí SinAdicciones.org para que esa ayuda esté disponible
                                    en Chile y en toda Latinoamérica:{" "}
                                    <strong className="text-[var(--warning)]">
                                        no como discurso, sino como recursos que se usan cuando yo no estoy.
                                    </strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============== PROYECTOS DE NELSON — links oficiales ============== */}
            <section
                className="py-20 md:py-28 bg-[#0a0a0a] border-b border-[var(--line)]"
                data-testid="sobre-proyectos"
            >
                <div className="container-x">
                    <div className="max-w-3xl mb-14">
                        <p className="eyebrow mb-4">Proyectos oficiales · dónde encontrarme</p>
                        <h2 className="font-display text-4xl md:text-6xl leading-[0.95]">
                            Todo lo que{" "}
                            <span className="text-[var(--danger)]">estoy construyendo</span>{" "}
                            para que la ayuda no dependa de mí.
                        </h2>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
                        {NELSON_LINKS.map((l, i) => {
                            const Icon = LINK_ICONS[l.key] || ExternalLink;
                            return (
                                <a
                                    key={l.key}
                                    href={l.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-[#0d0d0d] p-6 md:p-7 hover:bg-[#161616] transition-all group flex flex-col"
                                    data-testid={`nelson-link-${l.key}`}
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <Icon className="w-5 h-5 text-[var(--warning)]" />
                                        <ArrowUpRight className="w-4 h-4 text-[var(--ink-dim)] group-hover:text-[var(--danger)] transition-colors" />
                                    </div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)] mb-2">
                                        {String(i + 1).padStart(2, "0")} · Proyecto
                                    </p>
                                    <h3 className="font-display text-2xl text-[var(--paper-2)] mb-3 leading-tight group-hover:text-[var(--warning)] transition-colors">
                                        {l.label}
                                    </h3>
                                    <p className="font-mono text-xs text-[var(--paper)] leading-relaxed flex-1">
                                        {l.blurb}
                                    </p>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mt-5 pt-4 border-t border-dashed border-[var(--ink-dim)] break-all">
                                        {l.url.replace(/^https?:\/\//, "")}
                                    </p>
                                </a>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ============== CUENTOS PARA ADICTOS — obra literaria completa ============== */}
            <section
                id="cuentos"
                className="py-20 md:py-28 border-b border-[var(--line)] scroll-mt-32"
                data-testid="sobre-cuentos"
                itemScope
                itemType="https://schema.org/CreativeWorkSeries"
            >
                <meta itemProp="name" content="Cuentos para adictos" />
                <meta itemProp="author" content="Nelson González Rodríguez" />
                <meta itemProp="inLanguage" content="es" />
                <meta itemProp="numberOfItems" content="4" />

                <div className="container-x">
                    <div className="max-w-3xl mb-14">
                        <p className="eyebrow mb-4">Serie literaria · 4 cuentos · lectura gratuita</p>
                        <h2
                            className="font-display text-4xl md:text-6xl leading-[0.95] mb-6"
                            itemProp="alternativeHeadline"
                        >
                            <span className="text-[var(--warning)]">Cuentos para adictos</span>
                            <br />
                            <span className="text-[var(--paper-2)]">
                                — mi serie de 4 cuentos breves.
                            </span>
                        </h2>
                        <p
                            className="font-mono text-base text-[var(--paper)] leading-[1.75] mb-4"
                            itemProp="description"
                        >
                            Cada cuento toma una frase que se repite en tratamientos y grupos de
                            recuperación —<em className="text-[var(--warning)]"> "los dos lobos"</em>,{" "}
                            <em className="text-[var(--warning)]">"quemar las naves"</em>,{" "}
                            <em className="text-[var(--warning)]">"evita personas, lugares y cosas"</em>—
                            y empieza la historia justo donde esa frase termina. No las repite:
                            las continúa. Ahí aparece la pregunta que la frase original deja abierta.
                        </p>
                        <p className="font-mono text-sm text-[var(--ink-muted)] leading-relaxed">
                            Tres cuentos son para la persona con la adicción, en cualquier etapa. Uno
                            —<em>Papá, ¿qué es un adicto?</em>— es para leer en familia con niños y
                            adolescentes. Se leen gratis, sin registro. Publicados en{" "}
                            <a
                                href="https://www.sinadicciones.org/cuentos-para-adictos"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline text-[var(--paper-2)] hover:text-[var(--warning)]"
                            >
                                SinAdicciones.org/cuentos-para-adictos
                            </a>
                            .
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {CUENTOS.map((c, i) => (
                            <article
                                key={c.slug}
                                className="border border-[var(--line)] bg-[#0d0d0d] hover:border-[var(--warning)] transition-all group flex flex-col"
                                data-testid={`cuento-card-${c.slug}`}
                                itemProp="hasPart"
                                itemScope
                                itemType="https://schema.org/ShortStory"
                            >
                                <a
                                    href={c.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block relative aspect-[3/4] overflow-hidden bg-black"
                                    aria-label={`Leer '${c.title}' — cuento ${i + 1} de 4`}
                                >
                                    <img
                                        src={c.cover}
                                        alt={`Portada de '${c.title}' de Nelson González Rodríguez — ${c.subtitle}`}
                                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                                        loading="lazy"
                                        itemProp="image"
                                    />
                                    <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.25em] bg-black/80 text-[var(--warning)] px-2 py-1">
                                        {String(i + 1).padStart(2, "0")} / 04
                                    </span>
                                    {c.audience === "family" && (
                                        <span className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-[0.25em] bg-[var(--warning)] text-black px-2 py-1">
                                            Familia
                                        </span>
                                    )}
                                </a>
                                <div className="p-5 flex flex-col flex-1">
                                    <h3
                                        className="font-display text-xl md:text-2xl text-[var(--paper-2)] mb-2 leading-tight group-hover:text-[var(--warning)] transition-colors"
                                        itemProp="name"
                                    >
                                        {c.title}
                                    </h3>
                                    <p
                                        className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ink-muted)] mb-3"
                                        itemProp="alternativeHeadline"
                                    >
                                        {c.subtitle}
                                    </p>
                                    <p
                                        className="font-mono text-xs text-[var(--paper)] leading-relaxed mb-4 flex-1"
                                        itemProp="description"
                                    >
                                        {c.pitch}
                                    </p>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ink-dim)] mb-4 pt-3 border-t border-dashed border-[var(--ink-dim)]">
                                        <span className="text-[var(--danger)]">Para:</span>{" "}
                                        <span className="normal-case tracking-normal text-[var(--ink-muted)]">
                                            {c.forWhom}
                                        </span>
                                    </p>
                                    <a
                                        href={c.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--warning)] hover:text-[var(--paper-2)]"
                                        itemProp="url"
                                        data-testid={`cuento-cta-${c.slug}`}
                                    >
                                        Leer completo
                                        <ArrowUpRight className="w-3.5 h-3.5" />
                                    </a>
                                </div>
                            </article>
                        ))}
                    </div>

                    <div className="mt-12 text-center">
                        <a
                            href="https://www.sinadicciones.org/cuentos-para-adictos"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="sobre-cuentos-verlos-todos"
                        >
                            Ver la serie completa en SinAdicciones.org
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>
                </div>
            </section>

            {/* ============== ANTITERAPIA / VOZ CRUDA — pull-quotes ============== */}
            <section
                className="py-20 md:py-28 grain"
                data-testid="sobre-antiterapia"
            >
                <div className="container-x max-w-4xl">
                    <p className="eyebrow mb-6">
                        Voz cruda · lo que también soy
                    </p>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-12 leading-[0.95]">
                        No siempre consumí{" "}
                        <span className="text-[var(--danger)]">
                            para volar alto.
                        </span>
                    </h2>

                    <div className="prose-dark space-y-6 mb-12">
                        <p>
                            A veces lo hacía para no sentir el suelo. Para llenar un vacío con
                            mi firma y apellido. Y por eso, además de las plataformas
                            profesionales, dejo constancia también en primera persona: mis
                            cicatrices pidieron tribuna y aquí sus palabras.
                        </p>
                    </div>

                    {/* Pull quotes grid */}
                    <ul className="grid md:grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)] my-12">
                        {PULL_QUOTES.map((q, i) => (
                            <li
                                key={i}
                                className="bg-[#0d0d0d] p-6 md:p-8"
                                data-testid={`pull-quote-${i}`}
                            >
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-2">
                                    Buscaba ↓
                                </p>
                                <p className="font-display italic text-xl md:text-2xl text-[var(--paper-2)] leading-snug">
                                    {q}
                                </p>
                            </li>
                        ))}
                    </ul>

                    <div className="prose-dark space-y-6">
                        <p>
                            <strong>Cinco centros de rehabilitación</strong> después
                            —conductuales con <em>"ta mal" gritones</em> que elevan
                            cortisol; cognitivos que explican la dopamina como la gran
                            estafadora—, recaídas que parecían <em>reboots fallidos</em>…
                            aquí estoy. Escribiendo, construyendo y dejando herramientas
                            para que otro no tenga que pasar por el mismo laberinto sin mapa.
                        </p>
                    </div>
                </div>
            </section>

            {/* ============== MISIÓN ============== */}
            <section
                className="py-20 md:py-28 grain bg-[#0a0a0a]"
                data-testid="sobre-mision"
            >
                <div className="container-x max-w-4xl">
                    <p className="eyebrow mb-6">Lo que no busco · lo que sí</p>
                    <div className="grid md:grid-cols-2 gap-10">
                        <article className="border border-[var(--danger)] p-6 md:p-8">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)] mb-3">
                                No busco
                            </p>
                            <h3 className="font-display text-3xl mb-4 text-[var(--paper-2)]">
                                Ser el vocero.
                            </h3>
                            <p className="font-mono text-sm text-[var(--ink-muted)] leading-relaxed">
                                No soy terapeuta de cabecera ni influencer de la recuperación.
                                No vendo redención ni cursos motivacionales. No busco protagonismo:
                                busco que la ayuda no dependa de que yo esté.
                            </p>
                        </article>
                        <article className="border border-[var(--warning)] p-6 md:p-8 bg-[#0a0a0a]">
                            <p
                                className="font-mono text-[10px] uppercase tracking-[0.3em] mb-3"
                                style={{ color: "var(--warning)" }}
                            >
                                Sí busco
                            </p>
                            <h3 className="font-display text-3xl mb-4 text-[var(--paper-2)]">
                                Dejar herramientas.
                            </h3>
                            <p className="font-mono text-sm text-[var(--paper)] leading-relaxed">
                                Informar a toda la región. Darle a quien sufre —y a su familia—
                                un mapa claro: alternativas reales de tratamiento, guías paso a
                                paso y herramientas gratuitas que se puedan usar cuando yo no
                                estoy.
                            </p>
                        </article>
                    </div>

                    <div className="mt-16 border-t border-dashed border-[var(--ink-dim)] pt-12 text-center">
                        <p className="font-hand text-3xl md:text-4xl text-[var(--warning)] -rotate-2 inline-block mb-6">
                            Si algo de lo que lees te incomoda…
                        </p>
                        <p className="font-display italic text-2xl md:text-3xl text-[var(--paper-2)] max-w-2xl mx-auto leading-tight mb-8">
                            te refleja o te hace pausar el scroll… tranquilo,{" "}
                            <span className="text-[var(--danger)]">
                                ya empezó el primer paso.
                            </span>
                        </p>
                    </div>
                </div>
            </section>

            {/* ============== SOCIALS + CTA LIBRO ============== */}
            <section className="py-16 bg-[#0d0d0d] border-y border-[var(--line)]">
                <div className="container-x text-center">
                    <p className="eyebrow justify-center mb-6">
                        Redes y enlaces oficiales
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <a
                            href="https://www.linkedin.com/in/nelsongonzalezr/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="sobre-linkedin"
                        >
                            <Linkedin className="w-4 h-4" />
                            LinkedIn
                        </a>
                        <a
                            href={SITE_META.socials.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="sobre-instagram"
                        >
                            <Instagram className="w-4 h-4" />
                            Instagram
                        </a>
                        <a
                            href={SITE_META.socials.tiktok}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="sobre-tiktok"
                        >
                            <ExternalLink className="w-4 h-4" />
                            TikTok
                        </a>
                        <a
                            href={SITE_META.socials.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-brutal btn-brutal-ghost"
                            data-testid="sobre-youtube"
                        >
                            <Youtube className="w-4 h-4" />
                            YouTube
                        </a>
                        <a
                            href="https://sinadicciones.org"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-brutal btn-brutal-danger"
                            data-testid="sobre-sinadicciones"
                        >
                            SinAdicciones.org
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                        <Link
                            to="/contacto"
                            className="btn-brutal"
                            data-testid="sobre-contacto"
                        >
                            Escribirme
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {/* CTA fuerte al libro */}
                    <div className="mt-16 border-t border-dashed border-[var(--ink-dim)] pt-12 max-w-2xl mx-auto">
                        <p className="eyebrow justify-center mb-4">Mi libro</p>
                        <p className="font-display text-3xl md:text-4xl text-[var(--paper-2)] mb-6 leading-tight">
                            Si llegaste hasta acá,{" "}
                            <span className="text-[var(--danger)]">
                                quizá te toque leer esto.
                            </span>
                        </p>
                        <p className="font-mono text-sm text-[var(--ink-muted)] mb-6 leading-relaxed">
                            &ldquo;El Principio del Mapa Inverso&rdquo; — 16 capítulos
                            donde lo que viví se convierte en algo que tal vez
                            te sirva.{" "}
                            <strong className="text-[var(--paper-2)]">100% gratis</strong>{" "}
                            — sin costo, sin condiciones, sin letra chica.
                        </p>
                        <Link
                            to="/descargar"
                            className="btn-brutal btn-brutal-danger inline-flex"
                            data-testid="sobre-cta-libro"
                        >
                            Descargar el libro
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {/* ShareBar */}
                    <div className="mt-12 max-w-2xl mx-auto text-left">
                        <ShareBar
                            url={profileUrl}
                            title="Nelson González Rodríguez · Autor y fundador de SinAdicciones.org"
                            text="Conocí el trabajo de Nelson González Rodríguez: autor de 'El Principio del Mapa Inverso', fundador de SinAdicciones.org y PlanSobrio.com. Herramientas gratuitas para conectar familias con tratamiento verificado en LatAm:"
                            hashtags={["NiTanAdicto", "SinAdicciones", "PlanSobrio", "MapaInverso"]}
                            source="sobre_page"
                            variant="dark"
                        />
                    </div>
                </div>
            </section>
        </>
    );
}
